import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Grid, Chip, Stack, alpha,
  Select, MenuItem, FormControl, InputLabel, Alert, CircularProgress,
  TextField, Checkbox, FormControlLabel, Accordion, AccordionSummary,
  AccordionDetails, Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, LinearProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  DeleteSweep as DeleteSweepIcon,
  Preview as PreviewIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const META_URL  = `${API_BASE}/strategies/api/signal-nullify/`;
const ACTION_URL = (action) => `${API_BASE}/strategies/api/signal-nullify/${action}/`;

const dark = {
  bg: '#0a0e14', card: '#111827', border: alpha('#00d4ff', 0.15),
  text: '#e2e8f0', muted: alpha('#e2e8f0', 0.5),
  accent: '#00d4ff', green: '#00ff88', red: '#ef4444', amber: '#f59e0b',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: dark.card, color: dark.text,
    '& fieldset': { borderColor: dark.border },
    '&:hover fieldset': { borderColor: dark.accent },
    '&.Mui-focused fieldset': { borderColor: dark.accent },
  },
  '& .MuiInputLabel-root': { color: dark.muted },
  '& .MuiInputLabel-root.Mui-focused': { color: dark.accent },
};

const selectSx = {
  bgcolor: dark.card, color: dark.text,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: dark.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: dark.accent },
  '& .MuiSvgIcon-root': { color: dark.muted },
};

const menuProps = { PaperProps: { sx: { bgcolor: dark.card, color: dark.text } } };

const DIRECTION_COLOR = { LONG: dark.green, SHORT: dark.red };

export default function SignalNullify() {
  const [meta, setMeta] = useState(null);
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaError, setMetaError] = useState('');

  // form state
  const [timeframe, setTimeframe]     = useState('1m');
  const [asset, setAsset]             = useState('NQ');
  const [tradingHour, setTradingHour] = useState('ETH');
  const [fromTime, setFromTime]       = useState('');
  const [toTime, setToTime]           = useState('');
  const [selected, setSelected]       = useState(new Set());

  // action state
  const [preview, setPreview]         = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [executing, setExecuting]     = useState(false);
  const [result, setResult]           = useState(null);
  const [error, setError]             = useState('');

  const fetchMeta = useCallback(async () => {
    setMetaLoading(true); setMetaError('');
    try {
      const resp = await fetch(META_URL);
      if (!resp.ok) throw new Error('Failed to load signal metadata');
      setMeta(await resp.json());
    } catch (e) { setMetaError(e.message); }
    finally { setMetaLoading(false); }
  }, []);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  const toggleSignal = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const toggleCategory = (signals, categorySignals) => {
    const names = categorySignals.map(s => s.name);
    const allSelected = names.every(n => selected.has(n));
    setSelected(prev => {
      const next = new Set(prev);
      names.forEach(n => allSelected ? next.delete(n) : next.add(n));
      return next;
    });
  };

  const selectAll = () => {
    if (!meta) return;
    const all = Object.values(meta.categories).flat().map(s => s.name);
    setSelected(new Set(all));
  };

  const clearAll = () => setSelected(new Set());

  const buildBody = () => ({
    timeframe, asset, trading_hour: tradingHour,
    signals: Array.from(selected),
    from_time: fromTime || null,
    to_time: toTime || null,
  });

  const handlePreview = async () => {
    setError(''); setPreview(null); setResult(null);
    if (selected.size === 0) { setError('Select at least one signal to nullify'); return; }
    setPreviewLoading(true);
    try {
      const resp = await fetch(ACTION_URL('preview'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildBody()),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Preview failed');
      setPreview(data);
    } catch (e) { setError(e.message); }
    finally { setPreviewLoading(false); }
  };

  const handleExecute = async () => {
    setConfirmOpen(false); setExecuting(true); setError(''); setResult(null);
    try {
      const resp = await fetch(ACTION_URL('execute'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildBody()),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Execute failed');
      setResult(data);
      setPreview(null);
    } catch (e) { setError(e.message); }
    finally { setExecuting(false); }
  };

  const totalSelected = selected.size;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: dark.bg, p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: alpha(dark.red, 0.12), display: 'flex' }}>
          <DeleteSweepIcon sx={{ color: dark.red, fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={800} color={dark.text}>Signal Nullify</Typography>
          <Typography variant="body2" color={dark.muted}>
            Set signal columns to NULL for a specific table, asset, session, and optional time range
          </Typography>
        </Box>
      </Stack>

      {metaError && <Alert severity="error" sx={{ mb: 2 }}>{metaError}</Alert>}
      {metaLoading && <LinearProgress sx={{ mb: 2 }} />}

      {meta && (
        <Grid container spacing={3}>
          {/* ── Left: Filters + Signal Picker ─────────────────────── */}
          <Grid item xs={12} lg={7}>
            <Paper sx={{ bgcolor: dark.card, border: `1px solid ${dark.border}`, p: 2.5, mb: 2 }}>
              <Typography variant="subtitle2" color={dark.muted} fontWeight={700} mb={2}>
                TARGET
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ color: dark.muted }}>Timeframe</InputLabel>
                    <Select value={timeframe} onChange={e => setTimeframe(e.target.value)}
                      label="Timeframe" sx={selectSx} MenuProps={menuProps}>
                      {meta.timeframes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ color: dark.muted }}>Asset</InputLabel>
                    <Select value={asset} onChange={e => setAsset(e.target.value)}
                      label="Asset" sx={selectSx} MenuProps={menuProps}>
                      {meta.assets.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ color: dark.muted }}>Session</InputLabel>
                    <Select value={tradingHour} onChange={e => setTradingHour(e.target.value)}
                      label="Session" sx={selectSx} MenuProps={menuProps}>
                      {meta.trading_hours.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="From time (optional)"
                    placeholder="2026-01-01 09:30:00"
                    value={fromTime} onChange={e => setFromTime(e.target.value)} sx={fieldSx} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="To time (optional)"
                    placeholder="2026-04-20 16:00:00"
                    value={toTime} onChange={e => setToTime(e.target.value)} sx={fieldSx} />
                </Grid>
              </Grid>
            </Paper>

            {/* Signal picker */}
            <Paper sx={{ bgcolor: dark.card, border: `1px solid ${dark.border}`, p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="subtitle2" color={dark.muted} fontWeight={700}>
                  SIGNALS TO NULLIFY
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={selectAll}
                    sx={{ color: dark.accent, textTransform: 'none', fontSize: '0.72rem' }}>
                    Select all
                  </Button>
                  <Button size="small" onClick={clearAll}
                    sx={{ color: dark.muted, textTransform: 'none', fontSize: '0.72rem' }}>
                    Clear
                  </Button>
                </Stack>
              </Stack>

              {Object.entries(meta.categories).map(([category, signals]) => {
                const allCatSelected = signals.every(s => selected.has(s.name));
                const someCatSelected = signals.some(s => selected.has(s.name));
                return (
                  <Accordion key={category} disableGutters elevation={0}
                    sx={{ bgcolor: 'transparent', '&:before': { display: 'none' }, mb: 0.5 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: dark.muted }} />}
                      sx={{ px: 1, py: 0.5, minHeight: 36,
                        '& .MuiAccordionSummary-content': { my: 0.5 },
                        bgcolor: alpha('#fff', 0.02), borderRadius: 1,
                        border: `1px solid ${alpha('#fff', 0.05)}` }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Checkbox size="small" checked={allCatSelected}
                          indeterminate={someCatSelected && !allCatSelected}
                          onChange={() => toggleCategory(selected, signals)}
                          onClick={e => e.stopPropagation()}
                          sx={{ p: 0, color: dark.muted, '&.Mui-checked': { color: dark.accent },
                            '&.MuiCheckbox-indeterminate': { color: dark.amber } }} />
                        <Typography variant="body2" fontWeight={700} color={dark.text}
                          sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                          {category}
                        </Typography>
                        <Chip label={signals.length} size="small"
                          sx={{ bgcolor: alpha(dark.accent, 0.1), color: dark.accent,
                            fontSize: '0.65rem', height: 18 }} />
                        {someCatSelected && (
                          <Chip label={`${signals.filter(s => selected.has(s.name)).length} selected`}
                            size="small"
                            sx={{ bgcolor: alpha(dark.red, 0.15), color: dark.red,
                              fontSize: '0.65rem', height: 18 }} />
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 1, pt: 1, pb: 0.5 }}>
                      <Stack direction="row" flexWrap="wrap" gap={0.75}>
                        {signals.map(s => {
                          const isSelected = selected.has(s.name);
                          return (
                            <Chip key={s.name}
                              label={s.name}
                              size="small"
                              clickable
                              onClick={() => toggleSignal(s.name)}
                              sx={{
                                fontFamily: 'monospace', fontSize: '0.68rem',
                                bgcolor: isSelected
                                  ? alpha(dark.red, 0.2)
                                  : alpha('#fff', 0.04),
                                color: isSelected
                                  ? dark.red
                                  : DIRECTION_COLOR[s.direction] || dark.muted,
                                border: `1px solid ${isSelected ? alpha(dark.red, 0.5) : 'transparent'}`,
                                '&:hover': { bgcolor: alpha(dark.red, 0.15) },
                              }} />
                          );
                        })}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Paper>
          </Grid>

          {/* ── Right: Summary + Actions ───────────────────────────── */}
          <Grid item xs={12} lg={5}>
            <Paper sx={{ bgcolor: dark.card, border: `1px solid ${dark.border}`, p: 2.5, mb: 2 }}>
              <Typography variant="subtitle2" color={dark.muted} fontWeight={700} mb={1.5}>
                SUMMARY
              </Typography>
              <Stack spacing={1}>
                {[
                  { label: 'Table', value: `signals_${timeframe}`, color: dark.accent },
                  { label: 'Asset', value: asset, color: dark.green },
                  { label: 'Session', value: tradingHour, color: dark.amber },
                  { label: 'From', value: fromTime || '— (all)', color: dark.muted },
                  { label: 'To', value: toTime || '— (all)', color: dark.muted },
                  { label: 'Signals selected', value: totalSelected, color: totalSelected > 0 ? dark.red : dark.muted },
                ].map(({ label, value, color }) => (
                  <Stack key={label} direction="row" justifyContent="space-between">
                    <Typography variant="caption" color={dark.muted}>{label}</Typography>
                    <Typography variant="caption" color={color} fontWeight={600}
                      sx={{ fontFamily: 'monospace' }}>{value}</Typography>
                  </Stack>
                ))}
              </Stack>

              {totalSelected > 0 && (
                <Box mt={1.5}>
                  <Typography variant="caption" color={dark.muted} display="block" mb={0.75}>
                    Columns to nullify:
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {Array.from(selected).map(s => (
                      <Chip key={s} label={s.toLowerCase()} size="small"
                        sx={{ bgcolor: alpha(dark.red, 0.15), color: dark.red,
                          fontFamily: 'monospace', fontSize: '0.65rem' }} />
                    ))}
                  </Stack>
                </Box>
              )}

              <Divider sx={{ my: 2, borderColor: dark.border }} />

              <Stack spacing={1.5}>
                <Button fullWidth variant="outlined" startIcon={previewLoading ? <CircularProgress size={14} /> : <PreviewIcon />}
                  onClick={handlePreview} disabled={previewLoading || totalSelected === 0}
                  sx={{ textTransform: 'none', borderColor: dark.accent, color: dark.accent, fontWeight: 700,
                    '&:hover': { bgcolor: alpha(dark.accent, 0.08) },
                    '&:disabled': { opacity: 0.4 } }}>
                  {previewLoading ? 'Previewing…' : 'Preview'}
                </Button>
                <Button fullWidth variant="contained"
                  startIcon={<DeleteSweepIcon />}
                  onClick={() => { if (totalSelected > 0) setConfirmOpen(true); }}
                  disabled={totalSelected === 0 || executing}
                  sx={{ textTransform: 'none', bgcolor: dark.red, fontWeight: 700,
                    '&:hover': { bgcolor: '#c0392b' }, '&:disabled': { opacity: 0.4 } }}>
                  Nullify Signals
                </Button>
              </Stack>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* Preview result */}
            {preview && (
              <Paper sx={{ bgcolor: dark.card, border: `1px solid ${alpha(dark.accent, 0.3)}`, p: 2 }}>
                <Typography variant="subtitle2" color={dark.accent} fontWeight={700} mb={1.5}>
                  PREVIEW
                </Typography>
                <Stack spacing={0.75} mb={1.5}>
                  {[
                    { label: 'Rows matching filter', value: preview.total_rows_in_filter },
                    { label: 'Rows with data to clear', value: preview.rows_with_data, highlight: true },
                  ].map(({ label, value, highlight }) => (
                    <Stack key={label} direction="row" justifyContent="space-between">
                      <Typography variant="caption" color={dark.muted}>{label}</Typography>
                      <Typography variant="caption" fontWeight={700}
                        color={highlight ? dark.amber : dark.text}>{value}</Typography>
                    </Stack>
                  ))}
                </Stack>
                <Typography variant="caption" color={dark.muted} display="block" mb={0.5}>
                  SQL (approximate)
                </Typography>
                <Box sx={{ bgcolor: '#0d1117', borderRadius: 1, p: 1.5, border: `1px solid ${dark.border}` }}>
                  <Typography component="pre"
                    sx={{ m: 0, fontSize: '0.68rem', color: '#a9b1d6', fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {preview.preview_sql}
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* Execute result */}
            {result && (
              <Paper sx={{ bgcolor: dark.card, border: `1px solid ${alpha(dark.green, 0.3)}`, p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                  <CheckCircleIcon sx={{ color: dark.green }} />
                  <Typography fontWeight={700} color={dark.green}>Done</Typography>
                </Stack>
                <Stack spacing={0.75}>
                  {[
                    { label: 'Rows updated', value: result.rows_updated },
                    { label: 'Table', value: result.table },
                    { label: 'Columns', value: result.columns_nullified?.join(', ') },
                  ].map(({ label, value }) => (
                    <Stack key={label} direction="row" justifyContent="space-between">
                      <Typography variant="caption" color={dark.muted}>{label}</Typography>
                      <Typography variant="caption" color={dark.text} fontWeight={600}
                        sx={{ fontFamily: 'monospace' }}>{value}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: dark.card, border: `1px solid ${alpha(dark.red, 0.4)}` } }}>
        <DialogTitle sx={{ color: dark.text, borderBottom: `1px solid ${dark.border}` }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningIcon sx={{ color: dark.amber }} />
            <Typography fontWeight={700}>Confirm Nullify</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will set <strong>{totalSelected} signal column{totalSelected !== 1 ? 's' : ''}</strong> to NULL
            in <strong>signals_{timeframe}</strong> for <strong>{asset} / {tradingHour}</strong>
            {fromTime || toTime ? ` between ${fromTime || '(start)'} and ${toTime || '(end)'}` : ' (all time)'}.
            This cannot be undone.
          </Alert>
          <Stack spacing={0.5}>
            {Array.from(selected).map(s => (
              <Typography key={s} variant="caption" color={dark.red} sx={{ fontFamily: 'monospace' }}>
                • {s.toLowerCase()}
              </Typography>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${dark.border}`, px: 2, py: 1.5 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ color: dark.muted, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleExecute}
            startIcon={executing ? <CircularProgress size={14} color="inherit" /> : <DeleteSweepIcon />}
            disabled={executing}
            sx={{ bgcolor: dark.red, textTransform: 'none', fontWeight: 700,
              '&:hover': { bgcolor: '#c0392b' } }}>
            {executing ? 'Running…' : 'Yes, Nullify'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
